/**
 * Ribbon Command Handlers
 * Pure handler implementations (no Office wiring).
 * These can be safely imported by Node scripts.
 */

/* global OfficeRuntime */

import { ping, getProxyBaseUrlInUse } from "../services/proxyClient";
import { writeTable } from "../excel/tables";
import { STORAGE_KEYS } from "../shared/constants";
import { logger } from "../shared/logger";
import type { PingResponse, StoredPingResult } from "../shared/types";

/**
 * Proxy ping - calls proxy and stores result
 */
export async function proxyPing(event: Office.AddinCommands.Event): Promise<void> {
  try {
    const response = await ping();

    // Store with metadata
    const stored: StoredPingResult = {
      ...response,
      stored_at_iso: new Date().toISOString(),
      stored_by: "commands",
    };

    await OfficeRuntime.storage.setItem(STORAGE_KEYS.LAST_PING_RESULT, JSON.stringify(stored));
    logger.log("Commands", "proxyPing stored result", "success");
  } catch (error) {
    logger.error("Commands", "proxyPing failed", error);
  } finally {
    event.completed();
  }
}

/**
 * Insert ping result into Excel table
 */
export async function insertPingResult(event: Office.AddinCommands.Event): Promise<void> {
  try {
    // Try to get stored result first
    let pingData: PingResponse;
    try {
      const storedJson = await OfficeRuntime.storage.getItem(STORAGE_KEYS.LAST_PING_RESULT);
      if (storedJson) {
        const stored = JSON.parse(storedJson as string) as StoredPingResult;
        pingData = {
          ok: stored.ok,
          message: stored.message,
          env: stored.env,
          now_iso: stored.now_iso,
          request_id: stored.request_id,
        };
        logger.log("Commands", "insertPingResult using stored result", "success");
      } else {
        throw new Error("No stored result");
      }
    } catch {
      // No stored result, call proxy
      pingData = await ping();
      logger.log("Commands", "insertPingResult called proxy", "success");
    }

    // Get proxy base URL for display
    const proxyBaseUrl = getProxyBaseUrlInUse();

    // Write to Excel table at active cell
    await writeTable({
      target: { kind: "activeCell" },
      tableName: "ProxyPing",
      headers: ["env", "message", "now_iso", "request_id", "base_url"],
      rows: [
        [
          pingData.env,
          pingData.message,
          pingData.now_iso,
          pingData.request_id,
          proxyBaseUrl,
        ],
      ],
    });

    logger.log("Commands", "insertPingResult completed", "success");
  } catch (error) {
    logger.error("Commands", "insertPingResult failed", error);
  } finally {
    event.completed();
  }
}
